import { useEffect, useState } from 'react'
import ModalComponent from '..'
import { getTimeAgo } from '../../../helpers'
import { useFetchListsQuery } from '../../../redux-toolkits/lists/list.slice'
import { SingleEssential } from '../../../redux-toolkits/lists/list.types'
import { QueryProps } from '../../../redux-toolkits/shared/shared.type'
import { Button } from '../../button'
import { Checkbox } from '../../inputs/Checkbox'
import { SearchInput } from '../../inputs/searchInput'
import { TableComponent } from '../../table'

type V = SingleEssential

export const SelectEssentials = ({
  maxSelection,
  open,
  essentials,
  setEssentials,
  section,
}: {
  maxSelection?: number
  open?: boolean
  essentials: V[]
  setEssentials: (essentials: V[]) => void
  section?: 'LIFESTYLE' | 'FOOD' | 'EVENT'
}) => {
  const [queryParam, setQueryParam] = useState<QueryProps>({
    page: 1,
    limit: 20,
    search: undefined,
  })

  const { data, isFetching } = useFetchListsQuery(
    {
      ...queryParam,
      status: 'PUBLISHED',
      type: section,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !open,
    },
  )

  return (
    <div
      className="
        flex flex-col gap-2
        cursor-pointer
        "
    >
      <div className="bg-white pb-2 !max-w-full sticky top-0 z-10">
        <SearchInput
          searchFunction={(search) => {
            setQueryParam({
              ...queryParam,
              search: search === '' ? undefined : search,
            })
          }}
          placeholder="Search Essential"
          className="!max-w-full"
        />
      </div>

      <div className="w-full flex-1 overflow-y-scroll">
        <TableComponent
          rows={(data?.data?.docs ?? []).map((item) => {
            return {
              id: item._id,
              content: [
                <Checkbox
                  className="!gap-6 !w-full"
                  disabled={
                    maxSelection !== undefined &&
                    essentials.length >= maxSelection &&
                    essentials.length != 1 &&
                    !essentials.map((c) => c._id).includes(item._id)
                  }
                  checkStatus={
                    essentials.map((c) => c._id)?.includes(item._id) ?? false
                  }
                  handleChange={(checked) => {
                    if (checked) {
                      const dataCopy: V[] = JSON.parse(
                        JSON.stringify(essentials),
                      )
                      if (
                        maxSelection !== undefined &&
                        dataCopy.length >= maxSelection
                      ) {
                        dataCopy.shift()
                      }
                      dataCopy.push(item)

                      setEssentials(dataCopy)
                    } else {
                      const dataCopy: V[] = JSON.parse(
                        JSON.stringify(essentials),
                      )
                      setEssentials(
                        dataCopy.filter(
                          (singleData) => item._id !== singleData._id,
                        ),
                      )
                    }
                  }}
                  key={item._id}
                  text={
                    <div className="flex !w-full gap-3 items-center">
                      <img
                        src={item.icon}
                        className="block w-[64px] h-[64px] object-cover rounded-md"
                        alt={`icon`}
                      />

                      <div>
                        <p className="flex-1 text-base text-black font-normal">
                          {item.title}
                        </p>

                        <p className="text-sm text-sec-black">
                          {getTimeAgo(item.createdAt)}
                        </p>
                      </div>
                    </div>
                  }
                />,
              ],
            }
          })}
          loading={isFetching}
          isEmpty={[0, undefined].includes(data?.data?.docs?.length)}
        />
      </div>
    </div>
  )
}

export const SelectEssentialsModal = ({
  open,
  setOpen,
  onCloseCallback,
  onSuccessCallback,
  selectedEssentials,
  maxSelection,
  loading = false,
  section,
}: {
  open: boolean
  setOpen: (val: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (val: V[]) => void
  selectedEssentials?: V[]
  maxSelection?: number
  loading?: boolean
  section?: 'LIFESTYLE' | 'FOOD' | 'EVENT'
}) => {
  const [essentials, setEssentials] = useState<V[]>([])

  useEffect(() => {
    if (open) {
      setEssentials(selectedEssentials ?? [])
    }
  }, [open])

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        onCloseCallback && onCloseCallback()
      }}
      title="Select Essentials"
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="flex flex-col gap-6">
        <SelectEssentials
          maxSelection={maxSelection}
          open={open}
          essentials={essentials}
          setEssentials={setEssentials}
          section={section}
        />

        <Button
          type="button"
          primary
          label="Continue"
          onClick={() => {
            onSuccessCallback && onSuccessCallback(essentials)
          }}
          loading={loading}
          disabled={loading}
          className="sticky bottom-0"
        />
      </div>
    </ModalComponent>
  )
}
